/**
 * FilePicker — design-system file chooser.
 *
 * The native `<input type="file">` is kept in the DOM but hidden (`className="hidden"`), so the
 * browser's own "Choose File / No file chosen" control never renders. A design-system outline
 * Button opens it, and the selection is listed underneath with a per-file remove button.
 *
 * `id` goes on the visible Button, so `<Label htmlFor={id}>` names it and focuses it on click.
 */
import { useRef, type ChangeEvent } from "react";
import { FileText, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface FilePickerProps {
  /** Applied to the visible button so a `<Label htmlFor>` points at it. */
  id: string;
  /** Native `accept` filter, e.g. ".pdf,.docx,.txt". */
  accept?: string;
  /** Allow selecting several files. New picks are appended (duplicates ignored). */
  multiple?: boolean;
  /** Currently selected files (controlled). */
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  /** Reject any file larger than this many bytes. */
  maxBytes?: number;
  /** Called with a human-readable message when a picked file is rejected. */
  onReject?: (message: string) => void;
  className?: string;
}

/** "1.2 MB", "340 KB", "12 B". */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}

/** Same file picked twice → same key (File objects differ per pick). */
function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

export function FilePicker({
  id,
  accept,
  multiple = false,
  files,
  onChange,
  disabled = false,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  maxBytes,
  onReject,
  className,
}: FilePickerProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasFiles = files.length > 0;

  const buttonText = multiple
    ? hasFiles
      ? "Add files"
      : "Choose files"
    : hasFiles
      ? "Replace file"
      : "Choose file";

  const handleInput = (e: ChangeEvent<HTMLInputElement>): void => {
    const picked = Array.from(e.target.files ?? []);
    // Reset so picking the same file again still fires `change`.
    e.target.value = "";
    if (picked.length === 0) return;

    let accepted = picked;
    if (maxBytes !== undefined) {
      const tooBig = picked.filter((f) => f.size > maxBytes);
      if (tooBig.length > 0) {
        accepted = picked.filter((f) => f.size <= maxBytes);
        const names = tooBig.map((f) => f.name).join(", ");
        onReject?.(
          `${names} ${tooBig.length === 1 ? "is" : "are"} larger than the ${formatFileSize(maxBytes)} limit.`,
        );
      }
    }
    if (accepted.length === 0) return;

    if (!multiple) {
      onChange([accepted[0]]);
      return;
    }
    const seen = new Set(files.map(fileKey));
    const merged = [...files];
    for (const f of accepted) {
      const key = fileKey(f);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(f);
      }
    }
    onChange(merged);
  };

  const remove = (index: number): void => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className={cn("space-y-2", className)}>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        data-testid={`${id}-input`}
        onChange={handleInput}
      />
      <Button
        id={id}
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-4 w-4" aria-hidden="true" />
        {buttonText}
      </Button>
      {hasFiles && (
        <ul className="space-y-1" aria-label="Selected files">
          {files.map((file, i) => (
            <li
              key={fileKey(file)}
              className="flex items-center gap-1.5 text-xs text-text-secondary"
            >
              <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 truncate text-text-primary">
                {file.name}
              </span>
              <span className="shrink-0">({formatFileSize(file.size)})</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto h-7 w-7 shrink-0 p-0"
                aria-label={`Remove ${file.name}`}
                disabled={disabled}
                onClick={() => remove(i)}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default FilePicker;
